/**
 * A key/value store that cannot throw.
 *
 * `localStorage` is not a property access that always succeeds. Safari in
 * private browsing throws on `setItem`, a browser with site data blocked throws
 * on the getter itself, and an in-app webview - which is where the guest journey
 * runs, because the QR code was scanned with a camera app - may have neither. A
 * throw during the first paint of a portal is a blank screen, so every read
 * degrades to `null` and every write degrades to a no-op: the app keeps working,
 * it just forgets.
 *
 * This was written four times before it lived here (refactoring-audit finding
 * 40): once per frontend, in four shapes. Two of them were the same six lines
 * with different comments.
 */

/**
 * The slice of a store this package needs, narrowed to two methods so a caller
 * can pass a plain object and so nothing here can reach for the rest of the Web
 * Storage API.
 *
 * It is a PORT rather than `Storage` because two surfaces have a store of their
 * own: the counter tablet writes every value twice, to `localStorage` and to a
 * year-long cookie, because a tab pinned to a counter for months is exactly the
 * profile a browser evicts (PRD BAK-025); and the guest storefront keeps an
 * in-memory copy so a blocked store still remembers within the visit.
 */
export interface KeyValueStore {
  read(key: string): string | null;
  write(key: string, value: string): void;
}

/** Reads a string, answering `null` for absent, blocked or throwing storage. */
export function readStoredString(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    // A portal that cannot read its storage still has to render.
    return null;
  }
}

/** Writes a string, silently. The value is a convenience, never a record. */
export function writeStoredString(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    /* Storage denied or full - the choice simply does not persist. */
  }
}

/**
 * The default store: this browser's `localStorage`, wrapped.
 *
 * A constant rather than a factory, because it holds nothing - both methods
 * reach for `globalThis.localStorage` at call time. That matters in a test
 * runner, where the DOM is installed after the modules are evaluated.
 */
export const browserStore: KeyValueStore = {
  read: readStoredString,
  write: writeStoredString,
};
